
import { Options, prop, Vue } from "vue-class-component";
import Accordion from "primevue/accordion";
import AccordionTab from "primevue/accordiontab";
import Skeleton from "primevue/skeleton";
import Badge from "primevue/badge";
import {
  KulionerMerchant,
  KulionerOrder,
  KulionerProduct,
  KulionerUpdateMerchantOrderSummary,
  UpdateMerchantOrderRequest
} from "@/domain/entities/Kulioner";
import {
  formatPriceRP,
  parsingErrorResponse
} from "@/app/infrastructures/misc/Utils";
import Checkbox from "primevue/checkbox";
import ModalUpdateOrder from "./modal-update-order.vue";
import { KulionerController } from "@/app/ui/controllers/KulionerController";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import {
  KulionerListRequest,
  KulionerUpdateMerchantOrderRequest
} from "@/data/payload/api/KulionerApiRequest";
import { computeKulionerTotals } from "../utils/totals";
import { generateSingleMerchantReceipt } from "./receipt";

class Props {
  modelValue = prop<KulionerMerchant[]>({
    default: () => [],
    required: true
  });
  merchantData = prop<KulionerMerchant[]>({
    default: () => [],
    required: true
  });
  merchantTransaction = prop<KulionerMerchant[]>({
    default: () => [],
    required: true
  });
  status = prop<string>({
    default: "",
    type: String
  });
  loading = prop<boolean>({
    default: false,
    type: Boolean
  });
  skeletonCount = prop<number>({
    default: 3,
    type: Number
  });
}

@Options({
  components: {
    Accordion,
    AccordionTab,
    Badge,
    Checkbox,
    ModalUpdateOrder,
    Skeleton
  },
  emits: ["update:modelValue", "update"]
})
class CardListKulionerMerchant extends Vue.with(Props) {
  computeKulionerTotals = computeKulionerTotals;
  mounted() {
    const data = this.modelValue.map(
      (merchant) =>
        new KulionerMerchant({
          ...merchant,
          products: merchant.products.map(
            (product) =>
              new KulionerProduct({
                ...product
              })
          )
        })
    );
    this.$emit("update:modelValue", data);
  }

  getBadgeStatus(item: KulionerMerchant): { label: string; severity: string } {
    const { totalActualPieces, totalPieces } = computeKulionerTotals([item]);

    if (!totalActualPieces && item.products.every((e) => e.isQuantityUpdated)) {
      return {
        label: "Stok habis",
        severity: "danger"
      };
    }

    if (
      totalActualPieces < totalPieces &&
      totalActualPieces &&
      item.products.every((e) => e.isQuantityUpdated)
    ) {
      return {
        label: `Sudah dibeli ${totalActualPieces}/${totalPieces}`,
        severity: "warning"
      };
    }
    if (
      totalActualPieces === totalPieces &&
      totalActualPieces &&
      item.products.every((e) => e.isQuantityUpdated)
    ) {
      return {
        label: `Sudah dibeli ${totalActualPieces}/${totalPieces}`,
        severity: "success"
      };
    }

    return {
      label: "Belum dibeli",
      severity: "info"
    };
  }
  isAllStockEmpty(item: KulionerMerchant): boolean {
    return item.products
      .filter((p) => !p.isQuantityUpdated)
      .every((e) => e.actualQuantity === 0 && e.stockEmpty);
  }
  onSetAllStockEmpty(value: boolean, item: KulionerMerchant, index: number) {
    this.modelValue[index] = new KulionerMerchant({
      ...item,
      products: item.products.map((item: KulionerProduct) => {
        if (!item.isQuantityUpdated) {
          return new KulionerProduct({
            ...item,
            actualQuantity: 0,
            totalPrice: 0,
            stockEmpty: true
          });
        }
        return item;
      })
    });
    this.$emit("update:modelValue", this.modelValue);
  }

  formatPriceRP = formatPriceRP;

  onDecrease(item: KulionerProduct) {
    if (item.actualQuantity) {
      item.actualQuantity -= 1;
      item.totalPrice = item.actualQuantity * item.price;

      this.$emit("update:modelValue", this.modelValue);
    }

    if (!item.actualQuantity) {
      item.stockEmpty = true;
    }
  }

  onInputQuantity(event: Event, item: KulionerProduct) {
    const value = Number((event.target as HTMLInputElement).value) || 0;

    item.actualQuantity = value > item.quantity ? item.quantity : value < 0 ? 0 : value;
    item.totalPrice = item.actualQuantity * item.price;

    if (!item.actualQuantity) {
      item.stockEmpty = true;
    }
    
    this.$emit("update:modelValue", this.modelValue);
  }

  onIncrease(item: KulionerProduct) {
    if (item.actualQuantity < item.quantity) {
      item.actualQuantity += 1;
      item.totalPrice = item.actualQuantity * item.price;

      this.$emit("update:modelValue", this.modelValue);
    }
  }

  openDownload = false;
  selectedMerchant = new KulionerMerchant();
  summaryMerchant = new KulionerUpdateMerchantOrderSummary();

  get cycleIds() {
    return (
      KulionerController.cycles.find((cycle) => {
        return cycle.cutOffTime === (this.$route.params.id as string);
      })?.id || KulionerController.cycles[0].id
    );
  }

  mappingSummaryUpdateData(item: KulionerMerchant) {
    const productsWithDiff = item.products.map((p) => ({
      ...p,
      diffQuantity: p.quantity - p.actualQuantity
    }));
    this.summaryMerchant = new KulionerUpdateMerchantOrderSummary({
      mustBuy: productsWithDiff.reduce((sum, p) => sum + p.quantity, 0),
      wasBuy: productsWithDiff.reduce((sum, p) => sum + p.actualQuantity, 0),
      notBuy: productsWithDiff.reduce((sum, p) => sum + p.diffQuantity, 0)
    });
  }
  onOpenUpdateMerchantOrder(item: KulionerMerchant) {
    this.selectedMerchant = item;
    this.mappingSummaryUpdateData(item);
    this.openDownload = true;
  }
  async onDownload() {
    try {
      this.openDownload = false;
      MainAppController.showLoading();
      await KulionerController.updateMerchantOrder(
        new KulionerUpdateMerchantOrderRequest({
          cycleId: this.cycleIds,
          merchantId: this.selectedMerchant.id,
          products: this.selectedMerchant.products
            .filter((p) => !p.isQuantityUpdated)
            .map(
              (item) =>
                new UpdateMerchantOrderRequest({
                  id: item.id,
                  quantity: item.actualQuantity
                })
            )
        })
      );
      this.$emit("update");
      this.openDownload = false;
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(error, "Update pesanan gagal!", this.onDownload)
      );
    } finally {
      MainAppController.closeLoading();
    }
  }

  onUpdateStockEmpty(value: boolean, item: KulionerProduct) {
    if (!value && !item.actualQuantity) return;
    if (value) {
      item.stockEmpty = true;
      item.actualQuantity = 0;
    }
    this.$emit("update:modelValue", this.modelValue);
  }

  async onPrintReceipt(event: Event, item: KulionerMerchant) {
    try {
      MainAppController.showLoading();
      event.stopPropagation();
      await this.$nextTick();
      await KulionerController.getKulionerMerchantList(
        new KulionerListRequest({
          viewAs: "MERCHANT",
          cycleId: this.cycleIds.join(","),
          status: this.status,
        })
      );
      await generateSingleMerchantReceipt(item);
    } catch (error) {
      console.log("error", error);
      MainAppController.showErrorMessage(
        parsingErrorResponse(error, "Gagal download pesanan", () => this.onPrintReceipt(event, item))
      );
    } finally {
      MainAppController.closeLoading();
    }
  }
}
export default CardListKulionerMerchant;
